import pino from 'pino';

/**
 * Turns thrown values / Firebase-style `{ code, message }` objects into a readable string
 * for console output (avoids "[object Object]" from template literals and pino browser transmit).
 */
export function formatUnknownError(value: unknown): string {
  if (value === null || value === undefined) {
    return String(value);
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Error) {
    return value.stack ?? `${value.name}: ${value.message}`;
  }
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>;
    const code = rec.code;
    const message = rec.message;
    if (typeof message === 'string' && typeof code === 'string') {
      return `${code}: ${message}`;
    }
    if (typeof message === 'string') {
      return message;
    }
    if (typeof code === 'string') {
      return code;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return Object.prototype.toString.call(value);
    }
  }

  return String(value);
}

export const LoggingLevels = {
  error: 5,
  warn: 4,
  info: 3,
  debug: 2,
  trace: 1,
};

const defaultLevel = 'info';
const classLoggers: pino.Logger[] = [];

const loggerOptions: pino.LoggerOptions = {
  name: 'Gambits Logger',
  customLevels: { ...LoggingLevels },
  useOnlyCustomLevels: true,
  level: defaultLevel,
  formatters: {
    bindings: (bindings: pino.Bindings) => {
      return { name: bindings.name };
    },
    level: (level: string) => {
      return { level };
    },
  },
};

export const createLogger = (name: string): pino.Logger => {
  const logger = pino({
    ...loggerOptions,
    name,
    browser: {
      asObject: true,
      transmit: {
        send: (level: string, event: any) => {
          const rawMessages: unknown[] = event.messages ?? [];
          const formatted = rawMessages.map((m) => formatUnknownError(m));
          const primary = formatted[0] ?? '(no message)';
          const tail = formatted
            .slice(1)
            .filter((s) => s.length > 0)
            .join(' | ');
          const logLine = `${event.ts} ${level}: [${name}] => ${primary}${tail ? ` | ${tail}` : ''}`;
          switch (level) {
            case 'debug':
              // eslint-disable-next-line no-console
              console.debug(logLine);
              break;
            case 'error':
              // eslint-disable-next-line no-console
              console.error(logLine);
              break;
            case 'warn':
              // eslint-disable-next-line no-console
              console.warn(logLine);
              break;
            case 'trace':
              // eslint-disable-next-line no-console
              console.trace(logLine);
              break;
            default:
              // eslint-disable-next-line no-console
              console.log(logLine);
              break;
          }
        },
      },
      write: () => {},
    },
  });

  classLoggers.push(logger);
  return logger;
};

export const logger = createLogger('Gambits Logger');

/**
 * Tries to detect logging level using VITE_LOG_LEVEL environment variable.
 */
export const detectLoggingLevel = (): void => {
  const logLevel = import.meta.env.VITE_DEFAULT_CLIENT_LOG_LEVEL;
  if (!logLevel) return;

  const possibleLevels = Object.keys(LoggingLevels);
  if (!possibleLevels.includes(logLevel)) {
    logger.warn(`VITE_LOG_LEVEL="${logLevel}" is not valid value. Valid values: [${possibleLevels}]`);
  } else {
    changeLogLevel(logLevel as keyof typeof LoggingLevels);
  }
};

/**
 * Changes log level to the specified level.
 */
export const changeLogLevel = (level: keyof typeof LoggingLevels): void => {
  const logLevels = Object.keys(LoggingLevels);
  if (!logLevels.includes(level)) {
    throw new TypeError(`"${level}" is not valid log level. Expected one of [${logLevels}].`);
  }
  logger.info(`Log level changed to "${level}"`);
  loggerOptions.level = level;
  logger.level = level; // changing framework logger log level

  // changing class loggers log level
  classLoggers.forEach((l) => (l.level = level));
};
